import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import config from '../config/environment';
import apiService from '../services/api';
import { base64urlToUint8Array, uint8ArrayToBase64url } from '../utils/webauthn';

interface User {
  id: string;
  email: string;
  name: string; // Backend returns 'name', not 'first_name'/'last_name'
  role: string; // Role can be: user, admin, sysadmin, or translator_{langCode} (e.g., translator_es)
  is_verified: boolean; // Backend returns 'is_verified'
  is_active?: boolean; // Backend: false when org suspended; used to hide invites and block editing company users
  is_onboarded?: boolean; // Backend: true after onboarding completed or skipped; show modal only when false
  mode?: string; // "full" or "reader"
  company_id?: string; // Company ID for role-based access
  created_at?: string;
  updated_at?: string;
  // Additional fields from login response
  // 🔒 NOTE: Access/refresh tokens are now in httpOnly cookies, not stored here
  device_id?: string; // Device ID for this session
  device_name?: string; // Device name (e.g., "Chrome Browser")
  session_type?: string; // Session type (e.g., "web")
  key_backup_on_delete_allowed?: boolean; // Company users: allowed to request key backup before self-deletion (from profile)
  allow_file_key_download?: boolean; // Company users: can export single file key from assets/asset-edit (from profile)
  allow_multiple_file_key_download?: boolean; // Company users: can bulk export file keys (from profile)
  ownership_transfer_enabled?: boolean; // User-level: admin can disable; when false, hide transfer-owner in assets/asset-edit (from profile)
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  hasCheckedAuth: boolean;
  login: (email: string, password: string) => Promise<void>;
  loginWithPasskey: (userId: string) => Promise<void>;
  logout: () => Promise<void>;
  register: (userData: any) => Promise<void>;
  refreshToken: () => Promise<boolean>;
  refreshUserData: () => Promise<void>;
  checkAuthStatus: () => Promise<void>;
  clearTokens: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);

  // Helper function to get the correct API URL
  const getApiUrl = (endpoint: string) => {
    // Use relative URL in both development (via proxy) and production (our custom server)
    const url = `/api/v1${endpoint}`;
    
    console.log('AuthContext getApiUrl:', { endpoint, url, isDevelopment: config.isDevelopment, apiUrl: config.apiUrl });
    return url;
  };

  const checkAuthStatus = useCallback(async () => {
    // Inner attempt function so we can retry without a recursive useCallback.
    const attempt = async (isRetry: boolean): Promise<void> => {
      try {
        console.log(`🔒 AUTH: Checking authentication status${isRetry ? ' (retry)' : ''}...`);

        const response = await apiService.get('/auth/profile');
        const data = response.data;
        const userData = data.user;

        console.log('✅ AUTH: User authenticated via cookies:', userData.email);

        const deviceId = data.device_id || '';
        const deviceName = data.device_name || 'Web Browser';
        const sessionType = data.session_type || 'web';

        setUser({
          id: userData.id,
          email: userData.email,
          name: userData.name,
          role: userData.role,
          company_id: userData.company_id,
          is_verified: userData.is_verified,
          is_active: userData.is_active !== false,
          is_onboarded: userData.is_onboarded !== false,
          mode: userData.mode || 'full',
          created_at: userData.created_at,
          updated_at: userData.updated_at,
          device_id: deviceId,
          device_name: deviceName,
          session_type: sessionType,
          key_backup_on_delete_allowed: data.key_backup_on_delete_allowed === true,
          allow_file_key_download: data.allow_file_key_download === true,
          allow_multiple_file_key_download: data.allow_multiple_file_key_download === true,
          ownership_transfer_enabled: data.ownership_transfer_enabled !== false,
        });

        setHasCheckedAuth(true);
        setIsLoading(false);
      } catch (error: any) {
        const status: number | undefined = error?.response?.status;

        // 401 / 403 = genuine auth failure (token invalid, expired, or forbidden).
        // Clear the session so the user is sent to the login page.
        if (status === 401 || status === 403) {
          console.error('❌ AUTH: Auth check rejected by server (not authenticated):', status);
          clearTokens();
          setUser(null);
          setHasCheckedAuth(false);
          setIsLoading(false);
          return;
        }

        // 5xx / network error / timeout = transient backend problem.
        // Retry once after a short delay to cover connection pool warm-up on first load.
        // Cookies remain intact — do NOT clear them.
        if (!isRetry) {
          console.warn(`⚠️ AUTH: Transient error on auth check (${status ?? 'network'}), retrying in 1.5s…`);
          await new Promise(res => setTimeout(res, 1500));
          return attempt(true);
        }

        // Still failing after retry — unblock the UI without destroying the session.
        // Cookies are still valid; the next navigation or manual refresh will re-check.
        console.error('❌ AUTH: Auth check failed after retry (server/network error):', error);
        setIsLoading(false);
        // hasCheckedAuth intentionally left false so the app does not silently mark
        // the user as logged-out; the session will resolve on the next check.
      }
    };

    return attempt(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      console.log('🔒 AUTH: Logging in...', email);
      
      // 🔒 Use apiService (with automatic token refresh)
      const data = await apiService.login(email, password);
      console.log('✅ AUTH: Login successful, all data in httpOnly cookies');
      
      // 🔒 ALL sensitive data (tokens, device info) now in httpOnly cookies
      // No localStorage storage needed!
      
      // Set user with complete data from response
      setUser({
        id: data.user.id,
        email: data.user.email,
        name: data.user.name,
        role: data.user.role,
        company_id: data.user.company_id,
        is_verified: data.user.is_verified,
        is_active: data.user.is_active !== false,
        is_onboarded: data.user.is_onboarded !== false,
        mode: data.user.mode || 'full',
        created_at: data.user.created_at,
        updated_at: data.user.updated_at,
        device_id: data.device_id || '',
        device_name: data.device_name || 'Web Browser',
        session_type: data.session_type || 'web'
      });
      
      // Mark as authenticated
      setHasCheckedAuth(true);
      console.log('✅ AUTH: User state updated');
      
    } catch (error: any) {
      console.error('❌ AUTH: Login error:', error);
      // So Login page can show OTP form even if error.response is stripped by tooling
      if (error?.response?.data?.code === 'EMAIL_NOT_VERIFIED') {
        error.emailNotVerified = true;
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const loginWithPasskey = async (userId: string) => {
    try {
      setIsLoading(true);
      console.log('🔐 WebAuthn: Starting passkey authentication for user', userId);

      // Step 1: Begin authentication
      const beginResponse = await apiService.webauthnBeginAuthentication(userId);
      const { options, session_id } = beginResponse;

      console.log('🔐 WebAuthn: Received challenge, requesting user interaction...');

      // Step 2: Use browser WebAuthn API to get assertion
      const challenge = base64urlToUint8Array(options.publicKey.challenge);
      const credential = await navigator.credentials.get({
        publicKey: {
          challenge: challenge as BufferSource,
          allowCredentials: options.publicKey.allowCredentials?.map((cred: any) => {
            const id = base64urlToUint8Array(cred.id);
            return {
              id: id as BufferSource,
              type: cred.type,
              transports: cred.transports,
            };
          }),
          timeout: options.publicKey.timeout || 60000,
          userVerification: options.publicKey.userVerification || 'preferred',
        },
      }) as PublicKeyCredential;

      if (!credential) {
        throw new Error('User cancelled passkey authentication');
      }

      console.log('🔐 WebAuthn: User authenticated, sending assertion to server...');

      // Step 3: Convert response to WebAuthn JSON format (base64url-encoded strings)
      const response = credential.response as AuthenticatorAssertionResponse;
      const assertionResponse = {
        id: credential.id,
        rawId: uint8ArrayToBase64url(new Uint8Array(credential.rawId)),
        response: {
          authenticatorData: uint8ArrayToBase64url(new Uint8Array(response.authenticatorData)),
          clientDataJSON: uint8ArrayToBase64url(new Uint8Array(response.clientDataJSON)),
          signature: uint8ArrayToBase64url(new Uint8Array(response.signature)),
          userHandle: response.userHandle ? uint8ArrayToBase64url(new Uint8Array(response.userHandle)) : null,
        },
        type: credential.type,
      };

      // Step 4: Finish authentication
      const finishResponse = await apiService.webauthnFinishAuthentication(session_id, assertionResponse);

      if (!finishResponse.success) {
        throw new Error(finishResponse.message || 'Passkey authentication failed');
      }

      console.log('✅ WebAuthn: Authentication successful, setting user state...');

      // Step 5: Set user state (same as password login)
      if (finishResponse.user) {
        setUser({
          id: finishResponse.user.id,
          email: finishResponse.user.email,
          name: finishResponse.user.name,
          role: finishResponse.user.role,
          company_id: finishResponse.user.company_id,
          is_verified: finishResponse.user.is_verified,
          is_onboarded: finishResponse.user.is_onboarded !== false,
          mode: finishResponse.user.mode || 'full',
          created_at: finishResponse.user.created_at,
          updated_at: finishResponse.user.updated_at,
          device_id: finishResponse.device_id || '',
          device_name: finishResponse.device_name || 'Web Browser',
          session_type: finishResponse.session_type || 'web',
        });
        setHasCheckedAuth(true);
      } else {
        // If user not in response, fetch profile
        await checkAuthStatus();
      }

      console.log('✅ WebAuthn: Login successful');
    } catch (error: any) {
      console.error('❌ WebAuthn: Authentication error:', error);
      // Handle specific WebAuthn errors
      if (error.name === 'NotAllowedError') {
        throw new Error('Passkey authentication was cancelled or not allowed');
      } else if (error.name === 'InvalidStateError') {
        throw new Error('Passkey is already registered or invalid');
      } else if (error.name === 'NotSupportedError') {
        throw new Error('Passkeys are not supported in this browser');
      }
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      console.log('🔒 AUTH: Logging out...');
      // 🔒 Call logout endpoint to invalidate tokens and clear cookies
      await apiService.logout();
      console.log('✅ AUTH: Logout successful, cookies cleared by server');
    } catch (error) {
      console.error('❌ AUTH: Logout error:', error);
    } finally {
      // Clear local storage (non-sensitive data only)
      clearTokens();
      setUser(null);
      setHasCheckedAuth(false);
      console.log('✅ AUTH: Local data cleared');
    }
  };

  const register = async (userData: any) => {
    try {
      setIsLoading(true);
      
      const response = await fetch(getApiUrl('/auth/register'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        const text = await response.text();
        let errorMessage = 'Registration failed';
        try {
          const errorData = text ? JSON.parse(text) : {};
          errorMessage = errorData.error || errorMessage;
        } catch (_) {
          if (text) errorMessage = text;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      
      // Registration only returns a message, no tokens yet
      // User needs to verify email first
      console.log('Registration successful:', data.message);
      
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const refreshToken = useCallback(async (): Promise<boolean> => {
    // 🔒 Token refresh is now automatic via axios interceptor
    // This function is kept for API compatibility but does nothing
    console.log('🔄 AUTH: Manual refreshToken() called - automatic refresh is handled by axios interceptor');
    return true;
  }, []);

  const refreshUserData = useCallback(async (): Promise<void> => {
    try {
      console.log('🔄 AUTH: Refreshing user data...');
      
      // 🔒 Use apiService (with automatic token refresh)
      const response = await apiService.get('/auth/profile');
      const data = response.data;
      const userData = data.user;
      
      if (userData) {
        // Update user data with fresh data from server (including profile-level flags)
        setUser(prevUser => prevUser ? {
          ...prevUser,
          id: userData.id,
          email: userData.email,
          name: userData.name,
          role: userData.role,
          company_id: userData.company_id,
          is_verified: userData.is_verified,
          is_onboarded: userData.is_onboarded !== false,
          created_at: userData.created_at,
          updated_at: userData.updated_at,
          device_id: data.device_id || prevUser.device_id,
          device_name: data.device_name || prevUser.device_name,
          session_type: data.session_type || prevUser.session_type,
          key_backup_on_delete_allowed: data.key_backup_on_delete_allowed === true,
          allow_file_key_download: data.allow_file_key_download === true,
          allow_multiple_file_key_download: data.allow_multiple_file_key_download === true,
          ownership_transfer_enabled: data.ownership_transfer_enabled !== false,
        } : null);

        console.log('✅ AUTH: User data refreshed successfully');
      }
    } catch (error) {
      console.error('❌ AUTH: Error refreshing user data:', error);
    }
  }, []);

  const clearTokens = () => {
    // 🔒 Clear all localStorage (we don't store anything sensitive anymore)
    // (All auth data is in httpOnly cookies, cleared by backend)
    console.log('🔒 AUTH: Clearing local storage...');
    localStorage.clear();
    console.log('✅ AUTH: Local storage cleared');
  };

  useEffect(() => {
    // Check if user is logged in on app start
    checkAuthStatus();
    
    // 🔒 NOTE: Token refresh is now automatic via axios interceptor
    // No need for manual refresh interval anymore
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally empty to run only once on mount

  const value: AuthContextType = {
    user,
    isAuthenticated: hasCheckedAuth && !!user,
    isLoading,
    hasCheckedAuth,
    login,
    loginWithPasskey,
    logout,
    register,
    refreshToken,
    refreshUserData,
    checkAuthStatus,
    clearTokens,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
